import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ClaudeMessage } from "../types";
import { ClaudeContentArrayRenderer } from "./contentRenderer";
import {
  ClaudeToolUseDisplay,
  ToolExecutionResultRouter,
  MessageContentDisplay,
  AssistantMessageDetails,
} from "./messageRenderer";
import { extractClaudeMessageContent } from "../utils/messageUtils";
import { cn } from "../utils/cn";
import { COLORS } from "../constants/colors";
import { formatTime } from "../utils/time";

interface CollapsibleMessageNodeProps {
  message: ClaudeMessage;
  depth: number;
  isCollapsible: boolean;
  defaultExpanded: boolean;
}

/**
 * Extract preview text from message content
 */
const getPreviewText = (message: ClaudeMessage, maxLength: number = 100): string => {
  // Try to get text content
  const textContent = extractClaudeMessageContent(message);
  if (textContent) {
    const cleaned = textContent.replace(/\n+/g, " ").trim();
    if (cleaned.length > maxLength) {
      return cleaned.substring(0, maxLength) + "...";
    }
    return cleaned;
  }

  // Check for tool use
  if (message.toolUse) {
    const toolName = message.toolUse.name || "tool";
    return `🔧 ${toolName}`;
  }

  // Check for tool result
  if (message.toolUseResult) {
    return "📋 Tool execution result";
  }

  // Check for content array
  if (message.content && Array.isArray(message.content)) {
    const firstItem = message.content[0];
    if (firstItem && typeof firstItem === "object") {
      if ("type" in firstItem) {
        if (firstItem.type === "text" && "text" in firstItem) {
          const text = String(firstItem.text).replace(/\n+/g, " ").trim();
          if (text.length > maxLength) {
            return text.substring(0, maxLength) + "...";
          }
          return text;
        }
        if (firstItem.type === "tool_use" && "name" in firstItem) {
          return `🔧 ${firstItem.name}`;
        }
        if (firstItem.type === "tool_result") {
          return "📋 Tool result";
        }
      }
    }
  }

  return "...";
};

export const CollapsibleMessageNode: React.FC<CollapsibleMessageNodeProps> = ({
  message,
  depth,
  isCollapsible,
  defaultExpanded,
}) => {
  const { t } = useTranslation("components");
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (message.isSidechain) {
    return null;
  }

  const leftMargin = depth > 0 ? `ml-${Math.min(depth * 4, 16)}` : "";

  // User messages are always fully displayed
  if (!isCollapsible || message.type === "user") {
    return (
      <div
        className={cn(
          "w-full px-4 py-2",
          leftMargin,
          message.isSidechain && "bg-gray-100 dark:bg-gray-800"
        )}
      >
        <div className="max-w-4xl mx-auto">
          {import.meta.env.DEV && depth > 0 && (
            <div className="text-xs text-gray-400 dark:text-gray-600 mb-1">
              └─ {t("messageViewer.reply", { depth })}
            </div>
          )}

          <div
            className={`flex items-center space-x-2 mb-1 text-md text-gray-500 dark:text-gray-400 ${
              message.type === "user" ? "justify-end" : "justify-start"
            }`}
          >
            {message.type === "user" && (
              <div className="w-full h-0.5 bg-gray-100 dark:bg-gray-700 rounded-full" />
            )}
            <span className="font-medium whitespace-nowrap">
              {message.type === "user"
                ? t("messageViewer.user")
                : message.type === "assistant"
                ? t("messageViewer.claude")
                : t("messageViewer.system")}
            </span>
            <span className="whitespace-nowrap">
              {formatTime(message.timestamp)}
            </span>
            {message.isSidechain && (
              <span className="px-2 py-1 whitespace-nowrap text-xs bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-300 rounded-full">
                {t("messageViewer.branch")}
              </span>
            )}
            {message.type === "assistant" && (
              <div className="w-full h-0.5 bg-gray-100 dark:bg-gray-700 rounded-full" />
            )}
          </div>

          <div className="w-full">
            <MessageContentDisplay
              content={extractClaudeMessageContent(message)}
              messageType={message.type}
            />

            {message.content &&
              typeof message.content === "object" &&
              Array.isArray(message.content) &&
              (message.type !== "assistant" ||
                (message.type === "assistant" &&
                  !extractClaudeMessageContent(message))) && (
                <div className="mb-2">
                  <ClaudeContentArrayRenderer content={message.content} />
                </div>
              )}

            {!extractClaudeMessageContent(message) &&
              message.toolUseResult &&
              typeof message.toolUseResult === "object" &&
              Array.isArray(message.toolUseResult.content) && (
                <div className={cn("text-sm mb-2", COLORS.ui.text.tertiary)}>
                  <span className="italic">:</span>
                </div>
              )}

            {message.toolUse && (
              <ClaudeToolUseDisplay toolUse={message.toolUse} />
            )}

            {message.toolUseResult && (
              <ToolExecutionResultRouter
                toolResult={message.toolUseResult}
                depth={depth}
              />
            )}

            <AssistantMessageDetails message={message} />
          </div>
        </div>
      </div>
    );
  }

  // Collapsible non-user messages
  const previewText = getPreviewText(message);

  return (
    <div
      className={cn(
        "w-full px-4 py-2",
        leftMargin,
        message.isSidechain && "bg-gray-100 dark:bg-gray-800"
      )}
    >
      <div className="max-w-4xl mx-auto">
        {import.meta.env.DEV && depth > 0 && (
          <div className="text-xs text-gray-400 dark:text-gray-600 mb-1">
            └─ {t("messageViewer.reply", { depth })}
          </div>
        )}

        {/* Collapsible Header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            "w-full flex items-center justify-between",
            "rounded-lg border transition-all duration-200",
            "hover:bg-gray-50 dark:hover:bg-gray-800/50",
            isExpanded
              ? "border-gray-200 dark:border-gray-700 bg-transparent"
              : "border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30",
            "p-3"
          )}
        >
          <div className="flex items-center space-x-2 text-md text-gray-500 dark:text-gray-400">
            <span className="font-medium whitespace-nowrap">
              {message.type === "assistant"
                ? "🤖 " + t("messageViewer.claude")
                : t("messageViewer.system")}
            </span>
            <span className="whitespace-nowrap text-sm">
              {formatTime(message.timestamp)}
            </span>
            {message.isSidechain && (
              <span className="px-2 py-0.5 whitespace-nowrap text-xs bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-300 rounded-full">
                {t("messageViewer.branch")}
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {!isExpanded && (
              <span className={cn(
                "text-sm max-w-md truncate",
                COLORS.ui.text.tertiary
              )}>
                {previewText}
              </span>
            )}
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
            )}
          </div>
        </button>

        {/* Expanded Content */}
        <div
          className={cn(
            "overflow-hidden transition-all duration-300 ease-in-out",
            isExpanded ? "max-h-[5000px] opacity-100 mt-2" : "max-h-0 opacity-0"
          )}
        >
          <div className="w-full pl-2 border-l-2 border-gray-200 dark:border-gray-700">
            <MessageContentDisplay
              content={extractClaudeMessageContent(message)}
              messageType={message.type}
            />

            {message.content &&
              typeof message.content === "object" &&
              Array.isArray(message.content) &&
              (message.type !== "assistant" ||
                (message.type === "assistant" &&
                  !extractClaudeMessageContent(message))) && (
                <div className="mb-2">
                  <ClaudeContentArrayRenderer content={message.content} />
                </div>
              )}

            {!extractClaudeMessageContent(message) &&
              message.toolUseResult &&
              typeof message.toolUseResult === "object" &&
              Array.isArray(message.toolUseResult.content) && (
                <div className={cn("text-sm mb-2", COLORS.ui.text.tertiary)}>
                  <span className="italic">:</span>
                </div>
              )}

            {message.toolUse && (
              <ClaudeToolUseDisplay toolUse={message.toolUse} />
            )}

            {message.toolUseResult && (
              <ToolExecutionResultRouter
                toolResult={message.toolUseResult}
                depth={depth}
              />
            )}

            <AssistantMessageDetails message={message} />
          </div>
        </div>
      </div>
    </div>
  );
};

